import { Features } from 'c1gen/features'
import { Permissions } from 'c1gen/permissions'
import { Roles } from 'c1gen/roles'
import { RoleID } from 'lib/constants/system'
import { UserContext } from 'lib/context/app'
import { Status } from 'lib/rpc'
import { ActorObjectPermissions } from 'pbts/c1/api/iam/v1/authorization'
import { ReactElement, ReactNode, useCallback, useContext } from 'react'

// Allow checking permission or role, but not both
interface PermissionCapability {
    permission: Permissions
    role?: never
    children: ReactNode
}

interface RoleCapability {
    permission?: never
    role: Roles
    children: ReactNode
}

interface ObjectPermissionProps {
    permissions: ActorObjectPermissions | undefined
    children: ReactNode
}

interface ObjectPermissionChecker {
    ({ permissions, children }: ObjectPermissionProps): React.ReactElement | null
}

interface HasFeatureProps {
    feature: Features
    children: ReactNode
    fallback?: ReactElement | null
}

type Capability = PermissionCapability | RoleCapability

export const HasCapability = ({ permission, role, children }: Capability): React.ReactElement | null => {
    const hasPermission = useHasPermission(permission)
    const hasRole = useHasRole(role)

    if (hasPermission) {
        return <>{children}</>
    }

    if (hasRole) {
        return <>{children}</>
    }

    return null
}

export type featureChecker = (feature: Features) => boolean

/**
 * useUnsafeFeatureChecker returns a function that can be used to check a user's features
 * @deprecated use `useFeatureChecker` instead
 */
export const useUnsafeFeatureChecker = (): featureChecker => {
    const userContext = useContext(UserContext)

    return (feature: Features): boolean => {
        return userContext.features.has(feature)
    }
}

// Returns a classic tri-bool, null in the case that we haven't loaded features
export const useFeatureChecker = (feature: Features): boolean | null => {
    const { features, status } = useContext(UserContext)

    return status === Status.OK ? features.has(feature) : null
}

// Return true if ALL required features are present.
export const useHasRequiredFeatures = (requiredFeatures: Features[]): boolean | null => {
    const { features, status, waitForReady } = useContext(UserContext)

    waitForReady()

    // Also returns true if no features are required.
    return status === Status.OK ? requiredFeatures.every((feature) => features.has(feature)) : null
}

export const useFeatureCheckerLazy = (): ((feature: Features) => boolean | null) => {
    const { features, status } = useContext(UserContext)

    return useCallback(
        (feature: Features) => {
            return status === Status.OK ? features.has(feature) : null
        },
        [features, status],
    )
}

export const HasFeature = ({ feature, children, fallback }: HasFeatureProps): ReactElement | null => {
    const hasFeature = useUnsafeFeatureChecker()
    if (hasFeature(feature)) {
        return <>{children}</>
    }
    if (fallback !== undefined) {
        return fallback
    }
    return null
}

export type permissionChecker = (permission: Permissions) => boolean

export const usePermissionChecker = (): permissionChecker => {
    const userContext = useContext(UserContext)

    return (permission: Permissions): boolean => {
        return userContext.permissions.has(permission)
    }
}

export const usePermissionCheckerLazy = (): ((permission: Permissions) => boolean | null) => {
    const { permissions, status } = useContext(UserContext)

    return useCallback(
        (permission: Permissions) => {
            return status === Status.OK ? permissions.has(permission) : null
        },
        [permissions, status],
    )
}

export const useRoleCheckerLazy = (): ((role: Roles) => boolean | null) => {
    const { roles, status } = useContext(UserContext)

    return useCallback(
        (role: Roles) => {
            return status === Status.OK ? roles.has(role) : null
        },
        [roles, status],
    )
}

export const useHasPermission = (permission?: Permissions): boolean => {
    const userContext = useContext(UserContext)

    if (permission === undefined) {
        return false
    }
    return userContext.permissions.has(permission)
}

export const useHasAnyPermission = (permissions: Permissions[]): boolean => {
    const userContext = useContext(UserContext)
    return permissions.some((permission) => userContext.permissions.has(permission))
}

export const useHasPermissionAndStatusOk = (permission?: Permissions): [boolean, Status] => {
    const userContext = useContext(UserContext)

    if (permission === undefined) {
        return [false, userContext.status]
    }
    return [userContext.permissions.has(permission), userContext.status]
}

export type roleChecker = (role: Roles) => boolean
export const useRoleChecker = (): roleChecker => {
    const userContext = useContext(UserContext)
    return (role: Roles): boolean => {
        return userContext.roles.has(role)
    }
}

export const useHasRole = (role?: Roles): boolean => {
    const userContext = useContext(UserContext)

    userContext.waitForReady()

    if (role === undefined) {
        return false
    }

    return userContext.roles.has(role)
}

export const useHasAnyRole = (roles: Roles[]): boolean => {
    const userContext = useContext(UserContext)

    userContext.waitForReady()

    return roles.some((role) => userContext.roles.has(role))
}

export const useHasBuiltinRole = (role: RoleID): boolean => {
    const userContext = useContext(UserContext)

    userContext.waitForReady()

    return userContext.user.roleIds.includes(role)
}

export const useHasAnyBuiltinRole = (roles: RoleID[]): boolean => {
    const userContext = useContext(UserContext)

    userContext.waitForReady()

    return roles.some((role) => userContext.user.roleIds.includes(role))
}

const hasObjectPermissionFactory = (...actions: string[]): ObjectPermissionChecker => {
    return ({ permissions, children }: ObjectPermissionProps): React.ReactElement | null => {
        const matched = new Map<string, boolean>()
        for (const action of actions) {
            matched.set(action, false)
            switch (action) {
                case 'read':
                case 'edit':
                case 'delete':
                    if (permissions?.[action as keyof ActorObjectPermissions]) {
                        matched.set(action, true)
                    }
                    break

                default:
                    if (permissions?.extra?.[action]) {
                        matched.set(action, true)
                    }
            }
        }

        for (const v of matched.values()) {
            if (!v) {
                return null
            }
        }

        return <>{children}</>
    }
}
export const HasObjectPermissions = (...actions: string[]): ObjectPermissionChecker => {
    return hasObjectPermissionFactory(...actions)
}
export const HasObjectEditPermission = HasObjectPermissions('edit')

export const ReadonlyObjectPermissions = (): ActorObjectPermissions => {
    return { read: true, edit: false, delete: false, extra: {} }
}

export const ReadEditObjectPermissions = (): ActorObjectPermissions => {
    return { read: true, edit: true, delete: false, extra: {} }
}
